import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ensureCsrf, whatsappStatus } from '../lib/api';

// === Icons (replica mockup) ===
const MIIcon = ({ size = 16, stroke = 1.75, className = '', children }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size}
    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke}
    strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">{children}</svg>
);
const IconMapPin = (p) => (<MIIcon {...p}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></MIIcon>);
const IconArchive = (p) => (<MIIcon {...p}><rect x="2" y="3" width="20" height="5" rx="1" /><path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" /><line x1="10" y1="12" x2="14" y2="12" /></MIIcon>);
const IconEye = (p) => (<MIIcon {...p}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></MIIcon>);
const IconCheck = (p) => (<MIIcon {...p}><polyline points="20 6 9 17 4 12" /></MIIcon>);
const IconCalendar = (p) => (<MIIcon {...p}><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></MIIcon>);
const IconSearch2 = (p) => (<MIIcon {...p}><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></MIIcon>);
const IconX = (p) => (<MIIcon {...p}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></MIIcon>);
const IconWhatsApp = ({ size = 16, className = '' }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} className={className} aria-hidden="true">
    <path fill="currentColor" d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39-.094 0-.187-.044-.27-.087-.625-.355-1.215-.764-1.755-1.215-.566-.456-1.083-.987-1.495-1.594-.044-.084-.087-.176-.087-.27 0-.43 1.39-1.146 1.39-1.519 0-.176-.142-.41-.252-.583-.142-.226-.328-.518-.5-.78l-.515-.79c-.05-.083-.124-.166-.215-.166-.143 0-.276.038-.43.094-.156.06-.473.176-.732.32-.346.196-.65.43-.85.79-.196.353-.314.792-.314 1.197 0 .398.105.79.246 1.157.295.788.713 1.51 1.246 2.166.78.96 1.733 1.79 2.798 2.45.46.288.96.527 1.476.704.46.158.94.272 1.43.272.295 0 .59-.057.873-.18.296-.13.587-.343.85-.59.21-.196.367-.43.367-.7 0-.27-.094-.55-.27-.78-.165-.213-.4-.4-.65-.49-.245-.084-.5-.124-.756-.124zM16.01 4.998c-6.082 0-11.013 4.93-11.013 11.012 0 1.962.518 3.81 1.42 5.41L4.998 27.002l5.78-1.45c1.555.853 3.34 1.342 5.232 1.342 6.082 0 11.012-4.93 11.012-11.012 0-6.082-4.93-11.012-11.012-11.012zm0 20.116c-1.715 0-3.314-.49-4.667-1.318l-3.255.823.83-3.18c-.946-1.42-1.5-3.124-1.5-4.943 0-4.965 4.027-8.993 8.992-8.993s8.992 4.028 8.992 8.993-4.027 8.993-8.992 8.993z" />
  </svg>
);
const IconMail = (p) => (<MIIcon {...p}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></MIIcon>);

// === Helpers ===
const fmtEuro = (n) => n != null
  ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n)
  : '—';

const fmtDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return null; }
};
const fmtAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  const min = Math.floor(ms / 60000);
  if (min < 60) return `${min} min fa`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h fa`;
  const days = Math.floor(h / 24);
  if (days < 14) return `${days} g fa`;
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
};

// Mapping verdetto Rivelasta → forma+colore mockup (oro/verde/arancione/rosso)
const VERDETTO_MAP = {
  eccezionale:  { kind: 'oro',        label: 'Oro',        sub: 'Opportunità premium' },
  consigliata:  { kind: 'verde',      label: 'Verde',      sub: 'Affare valido' },
  con_riserva:  { kind: 'arancione',  label: 'Arancione',  sub: 'Attenzione richiesta' },
  da_evitare:   { kind: 'rosso',      label: 'Rosso',      sub: 'Sconsigliato' },
};

// Estrae codice catastale "A/1", "A/2", ecc. da tipologia text + label
function parseTipologia(t) {
  if (!t) return { code: '—', label: '' };
  const low = t.toLowerCase();
  if (/sign/.test(low))           return { code: 'A/1', label: 'Signorile' };
  if (/tipo civ/.test(low))       return { code: 'A/2', label: 'Civile' };
  if (/tipo eco/.test(low))       return { code: 'A/3', label: 'Economica' };
  if (/tipo pop/.test(low))       return { code: 'A/4', label: 'Popolare' };
  if (/villin/.test(low))         return { code: 'A/7', label: 'Villino' };
  if (/ville/.test(low))          return { code: 'A/8', label: 'Villa' };
  if (/uffic/.test(low))          return { code: 'A/10', label: 'Uffici' };
  if (/magazz/.test(low))         return { code: 'C/2', label: 'Magazzino' };
  if (/appartament/.test(low))    return { code: 'A/2', label: 'Appartamento' };
  return { code: '—', label: t.length > 30 ? t.slice(0, 30) + '…' : t };
}

const FILTERS = [
  { id: 'all', label: 'Tutti' },
  { id: 'new', label: 'Nuovi' },
  { id: 'to_contact', label: 'Da contattare' },
  { id: 'read', label: 'Letti' },
  { id: 'archived', label: 'Archiviati' },
];

const VerdettoBadge = ({ kind }) => {
  const v = VERDETTO_MAP[kind];
  const title = v ? `Verdetto Rivelasta: ${v.label} — ${v.sub}` : 'Non analizzato';
  return (
    <span className="mi-verdetto" data-verdetto={v?.kind || 'none'} title={title}>
      {v?.kind === 'oro' && (
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
          <polygon points="8,1.5 9.85,5.7 14.4,6.2 11,9.25 11.95,13.7 8,11.4 4.05,13.7 5,9.25 1.6,6.2 6.15,5.7" />
        </svg>
      )}
      {v?.kind === 'verde' && (
        <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><circle cx="8" cy="8" r="6" /></svg>
      )}
      {v?.kind === 'arancione' && (
        <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
          <rect x="3.4" y="3.4" width="9.2" height="9.2" transform="rotate(45 8 8)" />
        </svg>
      )}
      {v?.kind === 'rosso' && (
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
          <polygon points="8,2 14.3,13 1.7,13" />
        </svg>
      )}
      {!v && (
        <svg viewBox="0 0 16 16" width="10" height="10" aria-hidden="true"><circle cx="8" cy="8" r="4" fill="currentColor" /></svg>
      )}
    </span>
  );
};

// === Row ===
function PropertyRow({ g, onOpen, onSend, onArchive }) {
  const tipo = parseTipologia(g.immobile.tipologia);
  const pending = g.counts.pending;
  return (
    <div className="mi-row" data-unread={g.unread} onClick={() => onOpen(g)}>
      <div className="mi-row__verdetto">
        <VerdettoBadge kind={g.immobile.rivelasta_verdetto} />
      </div>

      <div className="mi-row__prop">
        <div className="mi-prop__addr">{g.immobile.indirizzo || '—'}</div>
        <div className="mi-prop__loc">
          <IconMapPin size={11} className="mi-prop__loc-icon" />
          <span>{g.immobile.comune || '—'}</span>
          {g.immobile.tribunale && <>
            <span className="mi-prop__sep">·</span>
            <span className="mi-prop__court">{g.immobile.tribunale}</span>
          </>}
        </div>
      </div>

      <div className="mi-row__tipo">
        <span className="mi-tipo">
          <span className="mi-tipo__code">{tipo.code}</span>
          <span className="mi-tipo__label">{tipo.label}</span>
        </span>
      </div>

      <div className="mi-row__price">
        <div className="mi-price__value">{fmtEuro(g.immobile.offerta_minima)}</div>
        <div className="mi-price__label">offerta min.</div>
      </div>

      <div className="mi-row__time">
        <div className="mi-time__received">{fmtAgo(g.received_at)}</div>
        {g.immobile.data_vendita && (
          <div className="mi-time__deadline">
            <IconCalendar size={10} />
            <span>scad. {fmtDate(g.immobile.data_vendita)}</span>
          </div>
        )}
      </div>

      <div className="mi-row__actions" onClick={(e) => e.stopPropagation()}>
        {pending > 0 ? (
          <button
            className="mi-act"
            title={`Invia a ${pending} lead da contattare`}
            aria-label={`Invia a ${pending} lead da contattare`}
            onClick={() => onSend(g)}
          >
            <IconWhatsApp size={14} />
          </button>
        ) : (
          <span className="mi-act mi-act--done" title="Tutti i lead contattati">
            <IconCheck size={13} />
          </span>
        )}
        <button className="mi-act" title="Apri scheda immobile" onClick={() => onOpen(g)}>
          <IconEye size={13} />
        </button>
        <button className="mi-act" title={g.archived ? 'Disarchivia' : 'Archivia'} onClick={() => onArchive(g)}>
          <IconArchive size={13} />
        </button>
      </div>
    </div>
  );
}

// === Send modal (WhatsApp + Email bulk) ===
function SendModal({ group, onClose, onSent }) {
  const pendingLeads = useMemo(() => group.leads.filter((l) => !l.contattato_at), [group]);
  const contactedLeads = useMemo(() => group.leads.filter((l) => l.contattato_at), [group]);

  // Canale: 'whatsapp' o 'email'. Default a whatsapp se ci sono lead con phone, altrimenti email.
  const [channel, setChannel] = useState(() =>
    pendingLeads.some((l) => l.phone) || !pendingLeads.some((l) => l.email) ? 'whatsapp' : 'email'
  );

  const hasContact = (l) => channel === 'whatsapp' ? !!l.phone : !!l.email;

  const [selected, setSelected] = useState(() => new Set(pendingLeads.filter(hasContact).map((l) => l.match_id)));
  const [query, setQuery] = useState('');
  const [waState, setWaState] = useState('unknown');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const tipo = parseTipologia(group.immobile.tipologia);
  const baseBody =
    `📍 ${group.immobile.indirizzo || ''}\n` +
    `   ${group.immobile.comune || ''}${tipo.code !== '—' ? ` · Tipologia ${tipo.code}` : ''}\n` +
    (group.immobile.offerta_minima ? `💶 Offerta minima ${fmtEuro(group.immobile.offerta_minima)}\n` : '') +
    (group.immobile.data_vendita ? `📅 Asta ${fmtDate(group.immobile.data_vendita)}\n` : '') +
    (group.immobile.url_pvp ? `\nScheda PVP:\n${group.immobile.url_pvp}\n` : '') +
    (group.immobile.url_report ? `\nReport Rivelasta:\n${group.immobile.url_report}\n` : '');

  const defaultWaMessage =
    `Ciao, abbiamo individuato un immobile in asta in linea con i tuoi criteri:\n\n` + baseBody + `\nRestiamo a disposizione.`;
  const defaultEmailSubject = `Nuovo immobile in asta — ${group.immobile.indirizzo || group.immobile.comune || 'compatibile'}`;
  const defaultEmailMessage =
    `Buongiorno,\n\nabbiamo individuato un immobile in asta in linea con i tuoi criteri:\n\n` + baseBody +
    `\nRestiamo a disposizione per qualsiasi informazione.\n\nQuesta email è inviata da un indirizzo no-reply: per risponderci, contattaci attraverso i canali abituali.`;

  const [message, setMessage] = useState(defaultWaMessage);
  const [subject, setSubject] = useState(defaultEmailSubject);

  // Quando il canale cambia, ripopola messaggio + selezione coerenti col canale.
  useEffect(() => {
    setMessage(channel === 'whatsapp' ? defaultWaMessage : defaultEmailMessage);
    setSelected(new Set(pendingLeads.filter(hasContact).map((l) => l.match_id)));
    setResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel]);

  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);

  useEffect(() => {
    whatsappStatus().then((s) => setWaState(s.state || 'unknown')).catch(() => setWaState('unknown'));
  }, []);

  const matchQuery = (l) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (l.name || '').toLowerCase().includes(q)
      || (l.phone || '').toLowerCase().includes(q)
      || (l.target || '').toLowerCase().includes(q);
  };
  const visiblePending = pendingLeads.filter(matchQuery);
  const visibleContacted = contactedLeads.filter(matchQuery);

  const visiblePendingIds = visiblePending.filter(hasContact).map((l) => l.match_id);
  const pendingSelectedCount = visiblePendingIds.filter((id) => selected.has(id)).length;
  const allPendingSelected = visiblePendingIds.length > 0 && pendingSelectedCount === visiblePendingIds.length;
  const somePendingSelected = pendingSelectedCount > 0 && !allPendingSelected;

  const toggleAllPending = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPendingSelected) visiblePendingIds.forEach((id) => next.delete(id));
      else visiblePendingIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const toggleOne = (matchId, eligible) => {
    if (!eligible) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(matchId)) next.delete(matchId);
      else next.add(matchId);
      return next;
    });
  };

  const handleSend = async () => {
    if (selected.size === 0) return;
    if (channel === 'whatsapp' && waState !== 'connected') {
      window.alert('WhatsApp non è connesso. Clicca sull\'icona WhatsApp in alto a destra per associare il numero.');
      return;
    }
    if (channel === 'email' && !subject.trim()) {
      window.alert('Inserisci un oggetto per la mail.');
      return;
    }
    setSending(true);
    setResult(null);
    try {
      await ensureCsrf();
      const url = channel === 'whatsapp'
        ? `/api/match-inbox/${group.immobile.id}/send`
        : `/api/match-inbox/${group.immobile.id}/send-email`;
      const payload = channel === 'whatsapp'
        ? { match_ids: Array.from(selected), message }
        : { match_ids: Array.from(selected), subject, message };
      const { data } = await api.post(url, payload);
      setResult(data);
      if (onSent) onSent(data);
      if ((data.failed || 0) === 0) {
        setTimeout(onClose, 2000);
      }
    } catch (e) {
      window.alert('Errore invio: ' + (e?.response?.data?.error || e?.message || 'unknown'));
    } finally {
      setSending(false);
    }
  };

  const selectedCount = selected.size;

  return (
    <div className="mi-modal" role="dialog" aria-modal="true">
      <div className="mi-modal__backdrop" onClick={onClose}></div>
      <div className="mi-modal__panel" onClick={(e) => e.stopPropagation()}>
        <header className="mi-modal__head">
          <div className="mi-modal__head-text">
            <div className="mi-modal__eyebrow">Invia ai lead compatibili</div>
            <h2 className="mi-modal__title">{group.immobile.indirizzo}</h2>
            <div className="mi-modal__meta">
              <span>{group.immobile.comune}</span>
              {group.immobile.tribunale && <><span className="mi-modal__metasep">·</span><span>{group.immobile.tribunale}</span></>}
              <span className="mi-modal__metasep">·</span>
              <span className="mi-tipo mi-tipo--inline">
                <span className="mi-tipo__code">{tipo.code}</span>
                <span className="mi-tipo__label">{tipo.label}</span>
              </span>
              {group.immobile.offerta_minima != null && (
                <>
                  <span className="mi-modal__metasep">·</span>
                  <span className="mi-modal__price">{fmtEuro(group.immobile.offerta_minima)}</span>
                </>
              )}
            </div>
          </div>
          <button className="mi-modal__close" onClick={onClose} aria-label="Chiudi">
            <IconX size={16} />
          </button>
        </header>

        {/* Channel toggle */}
        <div className="mi-modal__channels" style={{ display: 'flex', gap: 8, padding: '10px 16px', borderBottom: '1px solid var(--border, #e5e7eb)' }}>
          <button
            type="button"
            onClick={() => setChannel('whatsapp')}
            className="mi-modal__channel"
            data-active={channel === 'whatsapp'}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px',
              borderRadius: 999, border: '1px solid', cursor: 'pointer', fontSize: 13,
              borderColor: channel === 'whatsapp' ? '#16a34a' : 'var(--border, #e5e7eb)',
              background: channel === 'whatsapp' ? '#dcfce7' : 'transparent',
              color: channel === 'whatsapp' ? '#15803d' : 'inherit',
              fontWeight: channel === 'whatsapp' ? 600 : 400,
            }}
          >
            <IconWhatsApp size={14} />
            <span>WhatsApp</span>
          </button>
          <button
            type="button"
            onClick={() => setChannel('email')}
            className="mi-modal__channel"
            data-active={channel === 'email'}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px',
              borderRadius: 999, border: '1px solid', cursor: 'pointer', fontSize: 13,
              borderColor: channel === 'email' ? '#2563eb' : 'var(--border, #e5e7eb)',
              background: channel === 'email' ? '#dbeafe' : 'transparent',
              color: channel === 'email' ? '#1d4ed8' : 'inherit',
              fontWeight: channel === 'email' ? 600 : 400,
            }}
          >
            <IconMail size={14} />
            <span>Email</span>
          </button>
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--muted, #6b7280)' }}>
            {channel === 'whatsapp'
              ? `Lead con telefono: ${pendingLeads.filter((l) => l.phone).length}/${pendingLeads.length}`
              : `Lead con email: ${pendingLeads.filter((l) => l.email).length}/${pendingLeads.length}`}
          </span>
        </div>

        <div className="mi-modal__body">
          {/* Left: leads */}
          <div className="mi-modal__leads">
            <div className="mi-modal__leads-toolbar">
              <label className="mi-checkbox mi-checkbox--all">
                <input
                  type="checkbox"
                  checked={allPendingSelected}
                  ref={(el) => { if (el) el.indeterminate = somePendingSelected; }}
                  onChange={toggleAllPending}
                  disabled={visiblePendingIds.length === 0}
                />
                <span className="mi-checkbox__box"></span>
                <span className="mi-checkbox__lbl">
                  Seleziona tutti da contattare
                  <span className="mi-checkbox__count">{visiblePendingIds.length}</span>
                </span>
              </label>
              <div className="mi-modal__search">
                <IconSearch2 size={13} className="mi-modal__search-icon" />
                <input type="text" placeholder="Cerca lead…"
                  value={query} onChange={(e) => setQuery(e.target.value)} />
              </div>
            </div>

            <div className="mi-modal__leads-list">
              <div className="mi-group">
                <div className="mi-group__head">
                  <span className="mi-group__title">Da contattare</span>
                  <span className="mi-group__count">{visiblePending.length}</span>
                </div>
                {visiblePending.length === 0 ? (
                  <div className="mi-group__empty">Tutti i lead sono già stati contattati.</div>
                ) : (
                  <ul className="mi-group__list">
                    {visiblePending.map((l) => {
                      const eligible = hasContact(l);
                      const isSel = selected.has(l.match_id);
                      const contactStr = channel === 'whatsapp'
                        ? (l.phone || '(no telefono)')
                        : (l.email || '(no email)');
                      return (
                        <li key={l.match_id}>
                          <label className="mi-lead-item" data-selected={isSel}
                                 style={!eligible ? { opacity: 0.5 } : undefined}>
                            <span className="mi-checkbox">
                              <input type="checkbox" checked={isSel} disabled={!eligible}
                                     onChange={() => toggleOne(l.match_id, eligible)} />
                              <span className="mi-checkbox__box"></span>
                            </span>
                            <span className="mi-avatar mi-avatar--sm mi-avatar--neutral">{l.initials}</span>
                            <span className="mi-lead-item__main">
                              <span className="mi-lead-item__name">{l.name}</span>
                              <span className="mi-lead-item__target">{l.target}</span>
                            </span>
                            <span className="mi-lead-item__phone">{contactStr}</span>
                            <span className="mi-lead-item__status mi-lead-item__status--pending">
                              <span className="mi-status-dot"></span>
                              <span>da contattare</span>
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {contactedLeads.length > 0 && (
                <div className="mi-group mi-group--contacted">
                  <div className="mi-group__head">
                    <span className="mi-group__title">Già contattati</span>
                    <span className="mi-group__count">{visibleContacted.length}</span>
                  </div>
                  <ul className="mi-group__list">
                    {visibleContacted.map((l) => {
                      const eligible = hasContact(l);
                      const isSel = selected.has(l.match_id);
                      const contactStr = channel === 'whatsapp'
                        ? (l.phone || '(no telefono)')
                        : (l.email || '(no email)');
                      return (
                        <li key={l.match_id}>
                          <label className="mi-lead-item mi-lead-item--contacted" data-selected={isSel}
                                 style={!eligible ? { opacity: 0.5 } : undefined}>
                            <span className="mi-checkbox">
                              <input type="checkbox" checked={isSel} disabled={!eligible}
                                     onChange={() => toggleOne(l.match_id, eligible)} />
                              <span className="mi-checkbox__box"></span>
                            </span>
                            <span className="mi-avatar mi-avatar--sm mi-avatar--neutral">{l.initials}</span>
                            <span className="mi-lead-item__main">
                              <span className="mi-lead-item__name">{l.name}</span>
                              <span className="mi-lead-item__target">{l.target}</span>
                            </span>
                            <span className="mi-lead-item__phone">{contactStr}</span>
                            <span className="mi-lead-item__status mi-lead-item__status--done">
                              <IconCheck size={11} />
                              <span>il {fmtDate(l.contattato_at)}{l.contattato_via ? ` · ${l.contattato_via}` : ''}</span>
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Right: message */}
          <div className="mi-modal__msg">
            <div className="mi-modal__msg-head">
              <div className="mi-modal__msg-label">
                {channel === 'whatsapp' ? <IconWhatsApp size={14} className="mi-modal__wa" /> : <IconMail size={14} />}
                <span>{channel === 'whatsapp' ? 'Messaggio WhatsApp' : 'Email'}</span>
              </div>
              <button className="mi-modal__msg-reset" onClick={() => {
                setMessage(channel === 'whatsapp' ? defaultWaMessage : defaultEmailMessage);
                if (channel === 'email') setSubject(defaultEmailSubject);
              }}>
                Reset
              </button>
            </div>
            {channel === 'email' && (
              <input
                type="text"
                className="mi-modal__msg-area"
                style={{ marginBottom: 8, padding: '8px 10px', fontSize: 13 }}
                placeholder="Oggetto"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            )}
            <textarea
              className="mi-modal__msg-area"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={channel === 'email' ? 10 : 12}
            />
            <div className="mi-modal__msg-hint">
              {channel === 'whatsapp'
                ? (waState === 'connected'
                    ? 'Verrà inviato un messaggio individuale a ciascun lead selezionato.'
                    : <>WhatsApp <strong>non connesso</strong> — apri l'icona in alto a destra e scansiona il QR per associare l'organizzazione.</>)
                : <>Mittente: <code>Rivelasta &lt;noreply@dmoodlabs.com&gt;</code> · nessun Reply-To.</>}
            </div>
            {result && (
              <div style={{
                marginTop: 10,
                padding: 10,
                borderRadius: 8,
                background: result.failed === 0 ? 'oklch(0.96 0.06 150)' : 'oklch(0.96 0.06 90)',
                border: '1px solid oklch(0.85 0.10 150)',
                fontSize: 12,
              }}>
                Inviati: <strong>{result.sent}</strong> · Falliti: <strong>{result.failed}</strong>
              </div>
            )}
          </div>
        </div>

        <footer className="mi-modal__foot">
          <div className="mi-modal__foot-info">
            <strong>{selectedCount}</strong> {selectedCount === 1 ? 'lead selezionato' : 'lead selezionati'}
            {channel === 'whatsapp' && waState !== 'connected' && (
              <span style={{ marginLeft: 10, color: 'oklch(0.55 0.18 25)', fontSize: 12 }}>
                · WhatsApp non connesso
              </span>
            )}
          </div>
          <div className="mi-modal__foot-actions">
            <button className="btn btn--ghost" onClick={onClose}>Annulla</button>
            <button
              className="btn btn--solid"
              onClick={handleSend}
              disabled={
                sending ||
                selectedCount === 0 ||
                (channel === 'whatsapp' && waState !== 'connected') ||
                (channel === 'email' && !subject.trim())
              }
            >
              {channel === 'whatsapp' ? <IconWhatsApp size={13} /> : <IconMail size={13} />}
              <span>{sending ? 'Invio…' : `Invia a ${selectedCount}`}</span>
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

// === Main page ===
export default function MatchInbox() {
  const [groups, setGroups] = useState([]);
  const [counts, setCounts] = useState({ all: 0, new: 0, to_contact: 0, read: 0, archived: 0 });
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('recent');
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sendTarget, setSendTarget] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const PER_PAGE = 200;

  // Carica la pagina N. Se N=1 → rimpiazza, altrimenti → appende.
  const load = async (pageToLoad = 1) => {
    if (pageToLoad === 1) setLoading(true); else setLoadingMore(true);
    setError(null);
    try {
      const params = { filter, sort, page: pageToLoad, per_page: PER_PAGE };
      if (query) params.q = query;
      const { data } = await api.get('/api/match-inbox', { params });
      const newGroups = data.data || [];
      setGroups((prev) => pageToLoad === 1 ? newGroups : [...prev, ...newGroups]);
      setCounts(data.counts || {});
      setHasMore(!!data.pagination?.has_more);
      setPage(pageToLoad);
    } catch (e) {
      setError(e?.message || 'Errore caricamento');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Quando cambiano filter/sort/query → reset pagination + load page 1
  useEffect(() => {
    const t = setTimeout(() => load(1), query ? 250 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, sort, query]);

  // Sentinella per IntersectionObserver: appena entra in viewport → carica pagina successiva
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loadingMore || loading) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) load(page + 1);
    }, { rootMargin: '400px' /* prefetch poco prima del fondo */ });
    io.observe(sentinelRef.current);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loadingMore, loading, page]);

  const handleOpen = async (g) => {
    // Mark as viewed (fire and forget)
    if (g.unread) {
      try {
        await ensureCsrf();
        await api.post(`/api/match-inbox/${g.immobile.id}/viewed`);
        setGroups((gs) => gs.map((x) => x.immobile.id === g.immobile.id ? { ...x, unread: false } : x));
      } catch {}
    }
    // Apri il modale per inviare WhatsApp (semplificato — l'opzione "vedi scheda immobile" si potrà espandere dopo)
    setSendTarget(g);
  };

  const handleArchive = async (g) => {
    const archive = !g.archived;
    try {
      await ensureCsrf();
      await api.post(`/api/match-inbox/${g.immobile.id}/archive`, { archive });
      load();
    } catch (e) {
      window.alert('Errore archiviazione');
    }
  };

  const handleSent = () => {
    // Reload to reflect contattato_at change
    load();
  };

  return (
    <main className="content content--inbox">
      <div className="page__header">
        <div className="page__title-wrap">
          <h1 className="page__title">Match Inbox</h1>
          <p className="page__sub">
            Immobili in asta che hanno trovato corrispondenza con le richieste dei lead.
          </p>
        </div>
      </div>

      <div className="mi-toolbar">
        <div className="mi-tabs" role="tablist">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              className="mi-tab"
              data-active={filter === f.id}
              onClick={() => setFilter(f.id)}
              role="tab"
              aria-selected={filter === f.id}
            >
              <span>{f.label}</span>
              <span className="mi-tab__count">{counts[f.id] ?? 0}</span>
            </button>
          ))}
        </div>

        <div className="mi-toolbar__right">
          <div className="mi-search">
            <IconSearch2 size={14} className="mi-search__icon" />
            <input
              type="text"
              placeholder="Cerca per indirizzo, città, tipologia…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="mi-search__input"
            />
          </div>
          <div className="mi-sort">
            <label className="mi-sort__label" htmlFor="mi-sort-select">Ordina</label>
            <select
              id="mi-sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="mi-sort__select"
            >
              <option value="recent">Più recenti</option>
              <option value="old">Più vecchi</option>
              <option value="price_asc">Offerta min. ↑</option>
              <option value="price_desc">Offerta min. ↓</option>
              <option value="verdetto">Verdetto Rivelasta</option>
            </select>
          </div>

          {/* Toggle Lista / Mappa: cliccando "Mappa" naviga alla pagina /app/mappa
              filtrata sui match di questa org → stessa UX della pagina Immobili. */}
          <div role="tablist" aria-label="Visualizzazione" style={{
            display: 'inline-flex', padding: 2, background: 'var(--bg)',
            border: '1px solid var(--border)', borderRadius: 8, gap: 0,
          }}>
            <button
              type="button" role="tab" aria-selected={true}
              style={{
                padding: '5px 12px', fontSize: 12, fontWeight: 600,
                background: 'var(--bg-elev)', color: 'var(--fg)',
                border: '1px solid var(--border)', borderRadius: 6, cursor: 'default',
              }}
            >Lista</button>
            <button
              type="button" role="tab" aria-selected={false}
              onClick={() => navigate('/app/mappa?only_matched=1')}
              style={{
                padding: '5px 12px', fontSize: 12, fontWeight: 500,
                background: 'transparent', color: 'var(--fg-muted)',
                border: '1px solid transparent', borderRadius: 6, cursor: 'pointer',
              }}
            >Mappa</button>
          </div>
        </div>
      </div>

      <div className="mi-colhead">
        <div className="mi-colhead__verdetto" title="Verdetto Rivelasta"></div>
        <div className="mi-colhead__prop">Immobile</div>
        <div className="mi-colhead__tipo">Tipologia</div>
        <div className="mi-colhead__price">Offerta minima</div>
        <div className="mi-colhead__time">Ricevuto</div>
        <div className="mi-colhead__actions">Azione</div>
      </div>

      <div className="mi-list" role="list">
        {loading && (
          <div className="mi-empty">
            <div className="mi-empty__title">Caricamento…</div>
          </div>
        )}
        {error && !loading && (
          <div className="mi-empty">
            <div className="mi-empty__title">Errore</div>
            <div className="mi-empty__body">{error}</div>
          </div>
        )}
        {!loading && !error && groups.map((g) => (
          <PropertyRow
            key={g.immobile.id}
            g={g}
            onOpen={handleOpen}
            onSend={(g) => setSendTarget(g)}
            onArchive={handleArchive}
          />
        ))}

        {/* Sentinella per lazy loading */}
        {!loading && !error && hasMore && (
          <div ref={sentinelRef} style={{ padding: '16px 0', textAlign: 'center', color: 'var(--fg-muted)', fontSize: 12 }}>
            {loadingMore ? 'Caricamento altri immobili…' : ' '}
          </div>
        )}

        {!loading && !error && groups.length === 0 && (
          <div className="mi-empty">
            <div className="mi-empty__title">Nessun match corrisponde ai filtri</div>
            <div className="mi-empty__body">
              Prova a rimuovere il testo di ricerca o a cambiare lo stato.
              Per generare nuovi match vai su un lead e usa l'auto-suggest.
            </div>
          </div>
        )}
      </div>

      {sendTarget && (
        <SendModal group={sendTarget} onClose={() => setSendTarget(null)} onSent={handleSent} />
      )}
    </main>
  );
}
